import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Avatar,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  DirectionsCar as CarIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Payment as PaymentIcon,
  Badge as BadgeIcon,
  PersonPin as PersonPinIcon,
  Add as AddIcon,
  LocationOff as LocationOffIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { usersAPI } from '../services/api';

// Import the User interface from the API
import { User } from '../services/api';

// Extend the User interface with additional fields from the API response
interface UserWithRelations extends User {
  addresses?: Array<{
    address_id: number;
    address_type: 'home' | 'work' | 'other';
    address_line1: string;
    address_line2?: string;
    landmark?: string;
    city: string;
    state: string;
    country: string;
    postal_code: string;
    latitude: string;
    longitude: string;
    is_default: boolean;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    is_deleted: boolean;
  }>;
  
  cars?: Array<{
    car_id: number;
    user_id: number;
    brand_id: number;
    brand_name: string;
    model_id: number;
    model_name: string;
    number_plate: string;
    registration_year: number;
    car_photo: string | null;
    car_type: string;
    is_default: boolean;
    is_deleted: boolean;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  }>;
}

// Customer type definition for our component
interface Booking {
  booking_id: number;
  booking_number: string;
  user_id: number;
  vehicle_id: number;
  address_id: number;
  booking_date: string;
  slot_id: number;
  start_time: string;
  end_time: string;
  total_amount: string;
  discount_amount: string;
  tax_amount: string;
  tip_amount: number;
  final_amount: string;
  service_charges: string;
  addon_total: string;
  payment_status: string;
  payment_method: string;
  payment_id: string;
  status: string;
  cancellation_reason: string | null;
  refund_status: string | null;
  refund_amount: number | null;
  refund_id: string | null;
  assigned_staff_id: number;
  notes: string;
  contact_person_name: string;
  contact_person_number: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  cancelled_at: string | null;
  booking_otp: number;
  reschedule_status: number;
}

interface Transaction {
  payment_transaction_id: number;
  payment_id: number;
  booking_number: number;
  booking_id: number;
  user_id: number;
  amount: string;
  currency: string;
  payment_method: string;
  stripe_payment_intent: string | null;
  stripe_pay_id: string;
  stripe_transaction_id: string;
  client_secret: string;
  status: string;
  payment_date: string;
  transaction_response: string;
  created_at: string;
  updated_at: string;
}

interface CustomerDetails {
  user_id: number;
  user_type: 'admin' | 'staff' | 'customer';
  customer_type: 'Individual' | 'Business';
  name: string;
  email: string;
  mobile: string;
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  profile_photo: string | null;
  email_verified: number;
  mobile_verified: number;
  notification_enabled: number;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
  addresses: Array<{
    address_id: number;
    address_type: 'home' | 'work' | 'other';
    address_line1: string;
    address_line2: string;
    landmark: string;
    city: string;
    state: string;
    country: string;
    postal_code: string;
    latitude: string;
    longitude: string;
    is_default: number;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    is_deleted: string;
  }>;
  cars: Array<{
    car_id: number;
    user_id: number;
    brand_id: number;
    brand_name: string;
    model_id: number;
    model_name: string;
    number_plate: string;
    registration_year: number;
    car_photo: string | null;
    car_type: string;
    is_default: number;
    is_deleted: number;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  }>;
  bookings: Booking[];
  transactions: Transaction[];
}

interface Address {
  address_id: number;
  address_type: string;
  address_line1: string;
  address_line2?: string;
  landmark?: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  is_default: number;
}

interface Car {
  car_id: number;
  brand_name: string;
  model_name: string;
  number_plate: string;
  registration_year: number;
  car_photo?: string;
  car_type: string;
  is_default: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

interface TableTabPanelProps extends TabPanelProps {
  columns: { key: string; label: string; align?: 'left' | 'center' | 'right' }[];
  data: any[];
  renderCell?: (item: any, column: string) => React.ReactNode;
  emptyMessage?: string;
}

const TableTabPanel = (props: TableTabPanelProps) => {
  const { 
    value, 
    index, 
    columns, 
    data, 
    renderCell, 
    emptyMessage = 'No data available',
    ...other 
  } = props;

  return (
    <TabPanel value={value} index={index} {...other}>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell 
                  key={column.key} 
                  align={column.align || 'left'}
                  sx={{ fontWeight: 'bold' }}
                >
                  {column.label}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 ? (
              data.map((item, idx) => (
                <TableRow key={idx} hover>
                  {columns.map((column) => (
                    <TableCell key={`${idx}-${column.key}`} align={column.align || 'left'}>
                      {renderCell ? renderCell(item, column.key) : item[column.key]}
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    <IconButton size="small" color="primary">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </TabPanel>
  );
};

function a11yProps(index: number) {
  return {
    id: `customer-tab-${index}`,
    'aria-controls': `customer-tabpanel-${index}`,
  };
}

const CustomerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [tabValue, setTabValue] = useState(0);
  const [customer, setCustomer] = useState<CustomerDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const ucfirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (!id) {
        setError('Customer ID is required');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        console.log('Fetching customer details for ID:', id);
        
        // Fetch user data from the API with proper typing
        const response = await usersAPI.getById(parseInt(id));
        console.log('API Response:', response);
        
        if (!response) {
          throw new Error('No response from server');
        }
        
        // The API might be returning the user data in a data property
        const userData = (response as any)?.data || response;
        console.log('User Data:', userData);
        
        if (!userData) {
          throw new Error('Customer not found');
        }
        
        // Transform the API response to match our CustomerDetails interface
        const customerData: CustomerDetails = {
          user_id: userData.user_id || parseInt(id),
          user_type: userData.user_type || 'customer',
          customer_type: userData.customer_type || 'Individual',
          name: userData.name || '',
          email: userData.email || '',
          mobile: userData.mobile || '',
          status: userData.status || 'active',
          profile_photo: userData.profile_photo,
          email_verified: userData.email_verified || 0,
          mobile_verified: userData.mobile_verified || 0,
          notification_enabled: userData.notification_enabled || 0,
          created_at: userData.created_at || new Date().toISOString(),
          updated_at: userData.updated_at || new Date().toISOString(),
          last_login_at: userData.last_login_at || null,
          addresses: (userData.addresses || []).map((addr: any) => ({
            ...addr,
            address_line1: addr.address_line1 || '',
            address_line2: addr.address_line2 || '',
            landmark: addr.landmark || '',
            city: addr.city || '',
            state: addr.state || '',
            country: addr.country || '',
            postal_code: addr.postal_code || '',
            latitude: addr.latitude || '0',
            longitude: addr.longitude || '0',
            is_default: addr.is_default ? 1 : 0,
            created_at: addr.created_at || new Date().toISOString(),
            updated_at: addr.updated_at || new Date().toISOString(),
            deleted_at: addr.deleted_at || null,
            is_deleted: addr.is_deleted ? '1' : '0'
          })),
          cars: Array.isArray(userData.cars)
            ? userData.cars.map((car: any) => ({
                car_id: car.car_id || 0,
                user_id: car.user_id || parseInt(id),
                brand_id: car.brand_id || 0,
                brand_name: car.brand_name || '',
                model_id: car.model_id || 0,
                model_name: car.model_name || '',
                number_plate: car.number_plate || '',
                registration_year: car.registration_year || new Date().getFullYear(),
                car_photo: car.car_photo || null,
                car_type: car.car_type || 'Sedan',
                is_default: car.is_default ? 1 : 0,
                is_deleted: car.is_deleted ? 1 : 0,
                created_at: car.created_at || new Date().toISOString(),
                updated_at: car.updated_at || new Date().toISOString(),
                deleted_at: car.deleted_at || null
              }))
            : [],
          bookings: userData.bookings || [],
          transactions: userData.transactions || []
        };
        
        setCustomer(customerData);
        setError(null);
      } catch (err) {
        console.error('Error in fetchCustomerDetails:', {
          error: err,
          message: err instanceof Error ? err.message : 'Unknown error',
          stack: err instanceof Error ? err.stack : undefined
        });
        setError(`Error: ${err instanceof Error ? err.message : 'Failed to load customer details'}`);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCustomerDetails();
    }
  }, [id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDeleteCar = async (carId: number) => {
    if (window.confirm('Are you sure you want to remove this car?')) {
      try {
        setLoading(true);
        // Uncomment and implement the API call when ready
        // await api.delete(`/cars/${carId}`);
        
        // For now, just remove the car from the local state
        if (customer) {
          setCustomer({
            ...customer,
            cars: customer.cars.filter(car => car.car_id !== carId)
          });
        }
      } catch (error) {
        console.error('Error deleting car:', error);
        // You might want to show an error message to the user here
      } finally {
        setLoading(false);
      }
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status color based on status
  const getStatusColor = (status: string) => {
    const statusMap: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
      'completed': 'success',
      'confirmed': 'info',
      'cancelled': 'error',
      'pending': 'warning',
    };
    return statusMap[status.toLowerCase()] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !customer) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Customer not found'}
        </Alert>
        <Button
          component={Link}
          to="/customers"
          startIcon={<ArrowBackIcon />}
          variant="outlined"
        >
          Back to Customers
        </Button>
      </Box>
    );
  }

  const defaultAddress = customer.addresses.find(addr => addr.is_default) || customer.addresses[0];

  if (!customer) {
    return (
      <Box sx={{ p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          component={Link}
          to="/customers"
          startIcon={<ArrowBackIcon />}
          sx={{ mr: 2 }}
        >
          
        </Button>
       
       <Typography variant="h4" gutterBottom>
            Customer Details
        </Typography>

      </Box>

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Avatar
            src={customer.profile_photo || undefined}
            sx={{ width: 80, height: 80, mr: 3 }}
          >
            {customer.name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h5" component="div" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              {customer.name}
              <Chip
                label={customer.status}
                color={customer.status === 'active' ? 'success' : 'default'}
                size="small"
                sx={{ textTransform: 'capitalize' }}
              />
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              {customer.customer_type} Customer
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon color="action" fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  {customer.email}
                </Typography>
                {/*customer.email_verified ? (
                  <Chip
                    label="Verified"
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ ml: 1, height: 20 }}
                  />
                ) : null*/}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PhoneIcon color="action" fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  {customer.mobile}
                </Typography>
                {/*customer.mobile_verified ? (
                  <Chip
                    label="Verified"
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ ml: 1, height: 20 }}
                  />
                ) : null */}
              </Box>
              {defaultAddress && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationIcon color="action" fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    {defaultAddress.city}, {defaultAddress.state}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: { xs: 2, sm: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
              <span>Cars:</span>
              {customer.cars?.length > 0 ? (
                <Chip
                  label={customer.cars.length}
                  size="small"
                  sx={{ ml: 1 }}
                />
              ) : (
                <Chip label="0" size="small" sx={{ ml: 1 }} variant="outlined" />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
              <span>Bookings:</span>
              {customer.bookings?.length > 0 ? (
                <Chip
                  label={customer.bookings.length}
                  size="small"
                  sx={{ ml: 1 }}
                />
              ) : (
                <Chip label="0" size="small" sx={{ ml: 1 }} variant="outlined" />
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="customer details tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Basic Info" {...a11yProps(0)} />
            <Tab label="Addresses" {...a11yProps(1)} />
            <Tab label="Cars" {...a11yProps(2)} />
            <Tab label="Bookings" {...a11yProps(3)} />
            <Tab label="Transactions" {...a11yProps(4)} />
          </Tabs>
        </Box>

        {/* Basic Info Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Personal Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List>
                    <ListItem>
                      <ListItemIcon><PersonIcon /></ListItemIcon>
                      <ListItemText 
                        primary="Full Name" 
                        secondary={customer.name} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><EmailIcon /></ListItemIcon>
                      <ListItemText 
                        primary="Email" 
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {customer.email}
                           {/*customer.email_verified && (
                              <Chip
                                label="Verified"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ ml: 1, height: 20 }}
                              />
                            )*/}
                          </Box>
                        } 
                        secondaryTypographyProps={{ 
                          sx: { 
                            textOverflow: 'ellipsis', 
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center'
                          } 
                        }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><PhoneIcon /></ListItemIcon>
                      <ListItemText 
                        primary="Mobile" 
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {customer.mobile}
                            {/*customer.mobile_verified && (
                              <Chip
                                label="Verified"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ ml: 1, height: 20 }}
                              />
                            )*/}
                          </Box>
                        } 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><BadgeIcon /></ListItemIcon>
                      <ListItemText 
                        primary="Customer Type" 
                        secondary={customer.customer_type} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><PersonPinIcon /></ListItemIcon>
                      <ListItemText 
                        primary="Account Status"
                        secondary={
                          <Chip
                            label={customer.status && ucfirst(customer.status)}
                            color={customer.status === 'active' ? 'success' : 'default'}
                            size="small"
                          />
                        }
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {defaultAddress && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <HomeIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" component="div">
                        Default Address
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {defaultAddress.address_line1}
                      {defaultAddress.address_line2 && (
                        <>
                          <br />
                          {defaultAddress.address_line2}
                        </>
                      )}
                      {defaultAddress.landmark && (
                        <>
                          <br />
                          <span style={{ color: 'text.secondary' }}>Landmark: </span>
                          {defaultAddress.landmark}
                        </>
                      )}
                      <br />
                      {defaultAddress.city}, {defaultAddress.state}
                      <br />
                      {defaultAddress.country} - {defaultAddress.postal_code}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {customer.addresses.length > 0 ? (
            <Grid container spacing={3}>
              {customer.addresses.map((address) => (
                <Grid item xs={12} md={6} key={address.address_id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationIcon 
                            color={address.is_default ? 'primary' : 'action'} 
                            sx={{ mr: 1 }} 
                          />
                          <Typography variant="subtitle1">
                            {address.address_type.charAt(0).toUpperCase() + address.address_type.slice(1)}
                            {address.is_default && (
                              <Chip 
                                label="Default" 
                                size="small" 
                                color="primary" 
                                sx={{ ml: 1, height: 20 }} 
                              />
                            )}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {address.address_line1}
                        {address.address_line2 && (
                          <>
                            <br />
                            {address.address_line2}
                          </>
                        )}
                        {address.landmark && (
                          <>
                            <br />
                            <span style={{ color: 'text.secondary' }}>Landmark: </span>
                            {address.landmark}
                          </>
                        )}
                        <br />
                        {address.city}, {address.state}
                        <br />
                        {address.country} - {address.postal_code}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <LocationOffIcon color="disabled" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No addresses found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This customer hasn't added any addresses yet.
              </Typography>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          
          {customer.cars.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Car</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>License Plate</TableCell>
                    <TableCell>Year</TableCell>
                    <TableCell>Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customer.cars.map((car) => (
                    <TableRow key={car.car_id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {car.car_photo ? (
                            <Avatar src={car.car_photo} sx={{ mr: 2 }} />
                          ) : (
                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                              <CarIcon />
                            </Avatar>
                          )}
                          <Typography variant="body2">
                            {car.brand_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{car.model_name}</TableCell>
                      <TableCell>{car.number_plate}</TableCell>
                      <TableCell>{car.registration_year}</TableCell>
                      <TableCell>{car.car_type}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4, width: '100%' }}>
              <CarIcon color="disabled" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No cars found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This customer hasn't added any cars yet.
              </Typography>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          
          {customer.bookings.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Booking #</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customer.bookings.map((booking) => (
                    <TableRow key={booking.booking_id}>
                      <TableCell>{booking.booking_number}</TableCell>
                      <TableCell>{new Date(booking.booking_date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Chip 
                          label={booking.status} 
                          color={getStatusColor(booking.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{process.env.REACT_APP_SITE_CURRENCY}{parseFloat(booking.final_amount).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          component={Link}
                          to={`/bookings/${booking.booking_id}`}
                          color="primary"
                          size="small"
                          title="View Booking"
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ReceiptIcon color="disabled" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No bookings found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This customer hasn't made any bookings yet.
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* Transactions Tab */}
        <TabPanel value={tabValue} index={4}>
          {customer.transactions.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell>Booking #</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Payment Method</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customer.transactions.map((transaction) => (
                    <TableRow key={transaction.payment_transaction_id}>
                      <TableCell>{transaction.payment_id}</TableCell>
                      <TableCell>{transaction.booking_number}</TableCell>
                      <TableCell>{process.env.REACT_APP_SITE_CURRENCY}{parseFloat(transaction.amount).toFixed(2)}</TableCell>
                      <TableCell>{transaction.payment_method}</TableCell>
                      <TableCell>
                        <Chip 
                          label={transaction.status} 
                          color={
                            transaction.status.toLowerCase() === 'success' ? 'success' :
                            transaction.status.toLowerCase() === 'failed' ? 'error' :
                            'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{new Date(transaction.payment_date).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PaymentIcon color="disabled" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No transactions found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This customer doesn't have any transaction history yet.
              </Typography>
            </Box>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default CustomerDetails;
